import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { ApplicationProfile } from '@app/@shared/models/application-profile.model';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { CredentialsService } from '@app/@shared/services/credentials.service';
import { AccessType } from '@app/@shared/enums/access-types.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DashboardApiService } from './services/dashboard.api.service';
import { DateRange } from './utils/date-range.util';
import { buildOverviewTiles } from './utils/overview.util';
import { ActivityRowView } from './models/activity-report.model';

/**
 * Access types allowed to view the dashboard stats.
 * NOTE: the dashboard is the app's fallback redirect target, so it CANNOT be
 * guarded at the route level (AccessGuard would redirect here and loop). Access
 * is gated on the CONTENT via `canViewStats` instead.
 */
const DASHBOARD_ACCESS_TYPES: AccessType[] = [AccessType.LoyaltyProgramAdmin];

@UntilDestroy()
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  isLoading: boolean = true;
  canViewStats: boolean = false;
  private coalitionId: number | null = null;

  protected currentRange: DateRange | null = null;
  private currentTier = false;
  protected selectedRow: ActivityRowView | null = null;
  protected drillVisible = false;

  protected readonly loading$;
  protected readonly error$;
  protected readonly rows$;
  protected readonly tiles$;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private applicationProfileService: ApplicationProfileService,
    private credentialsService: CredentialsService,
    private dashboardApiService: DashboardApiService
  ) {
    this.loading$ = this.dashboardApiService.loading$;
    this.error$ = this.dashboardApiService.error$;
    this.rows$ = this.dashboardApiService.rows$;
    this.tiles$ = this.dashboardApiService.totals$.pipe(map((totals) => (totals ? buildOverviewTiles(totals) : [])));
  }

  ngOnInit(): void {
    this.canViewStats = this.credentialsService.hasAccessAny(DASHBOARD_ACCESS_TYPES);

    this.route.paramMap.pipe(untilDestroyed(this)).subscribe((params) => {
      const coalitionId = params.get('coalitionId');
      if (!coalitionId) {
        this.redirectToScopedDashboard();
        return;
      }
      this.coalitionId = Number(coalitionId);
      this.isLoading = false;
    });
  }

  /** Fired by the date tabs (including their initial default-year emit). */
  onRangeChange(range: DateRange): void {
    this.currentRange = range;
    this.reload();
  }

  onTierChange(isFreeTier: boolean): void {
    this.currentTier = isFreeTier;
    this.reload();
  }

  private reload(): void {
    if (this.coalitionId == null || this.currentRange == null) {
      return;
    }
    this.dashboardApiService
      .refresh(this.coalitionId, this.currentRange.from, this.currentRange.to, this.currentTier)
      .pipe(take(1), untilDestroyed(this))
      .subscribe();
  }

  /** Row selected for the per-merchant income drill-down. */
  onMerchantSelect(row: ActivityRowView): void {
    if (!this.currentRange) {
      return;
    }
    this.selectedRow = row;
    this.drillVisible = true;
  }

  private redirectToScopedDashboard(): void {
    this.applicationProfileService.applicationProfile$
      .pipe(take(1), untilDestroyed(this))
      .subscribe((profile: ApplicationProfile) => {
        this.router.navigate([`${profile.Representative.CoalitionId}/dashboard`]);
      });
  }
}
